import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { GENDERS } from 'components/intake/PatientInfoSection';
import { useEnvironment } from 'context/environment';
import { useAllowedFacilities } from 'hooks/useAllowedFacilities';
import { usePortalSystems } from 'hooks/usePortalSystems';
import { indexPortalConnections, portalConnectionQueryKeys } from 'services/api/portalConnections';
import { portalScriptQueryKeys, showPortalScript } from 'services/api/portalScripts';
import { CollectedFieldType, RetrievalFieldOption } from 'services/api/portalSystems';
import { saveWorkflowActionProgress, workflowQueryKeys } from 'services/api/workflows';
import { uploadPortalDocuments } from 'services/portalDocuments';
import {
  finishManualPortal,
  launchManualPortal,
  ManualPortalField,
  onManualPortalResult,
  runPortalRetrieval,
} from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';

import { ATTACHMENTS_VALUE_NAME, portalCollectionOutputs, retrievalFieldsFor } from '../constants';

// A retrieval field the typed form can render — everything but `documents`, which rides the
// capture affordance instead.
type TypedRetrievalField = RetrievalFieldOption & { type: Exclude<CollectedFieldType, 'documents'> };

export interface ManualLaunchRequest {
  workflowId: string;
  actionId: string;
  entryUrl: string;
  sourceSystem: string;
  // What the portal calls this patient, passed through so the HUD can help the person find them.
  portalPatientName?: string;
  // Record mode: the extension arms the recorder instead of the manual HUD — the person's
  // portal run records a fresh script, and the captured values stage on the action when
  // they finish. `produces` is the retrieval action's declared outputs, widened to the
  // collect-field set the same way every other retrieval mode is.
  record?: boolean;
  // Replay: the saved script to execute. The extension replays it against the live entry
  // URL and the captured values stage on the action, same tail as a recording.
  scriptId?: string;
  produces?: string[];
}

interface ManualLaunchState {
  // The task whose portal tab is open right now, or null. At most one, browser-wide: the
  // extension refuses a second manual job, so the UI arbitrates rather than letting a person
  // discover the refusal at click time.
  activeWorkflowId: string | null;
  launchingWorkflowId: string | null;
  // The task whose record-mode run is in flight, or null. Distinct from activeWorkflowId:
  // a recording has no launchId to close, and it ends only when the person finishes (or
  // abandons) in the portal tab — the values then stage on the action from here.
  recordingWorkflowId: string | null;
  // The task whose script replay is in flight, or null. Same lifecycle as a recording —
  // no launchId, ends when the extension's run resolves.
  replayingWorkflowId: string | null;
  // Tasks whose `Open in <portal>` has been acked this session. Session-scoped on purpose —
  // persisting it would need new run state, and a reload legitimately means "start over".
  unlockedWorkflowIds: Set<string>;
  launch: (request: ManualLaunchRequest) => Promise<void>;
  close: () => void;
}

const ManualLaunchContext = createContext<ManualLaunchState | null>(null);

/**
 * Owns the `Open in <portal>` launch for the whole page.
 *
 * It lives ABOVE the card list on purpose. `launchId` used to be component state on the one
 * card that offered the button; on a list that inserts and removes cards live, any refetch
 * that remounts a card would tear down the result subscription AND skip `manualClose`, leaving
 * the extension's single job slot occupied so every later launch fails.
 */
export function ManualLaunchProvider({ children }: { children: React.ReactNode }) {
  const { addToast } = useToastActions();
  const { environment } = useEnvironment();
  const queryClient = useQueryClient();
  const { facilities: allowedFacilities } = useAllowedFacilities();
  const { systems: portalSystems } = usePortalSystems();
  const [active, setActive] = useState<{ launchId: string; workflowId: string } | null>(null);
  const [launchingWorkflowId, setLaunchingWorkflowId] = useState<string | null>(null);
  const [scriptedRun, setScriptedRun] = useState<{ workflowId: string; mode: 'record' | 'replay' } | null>(null);
  const [unlockedWorkflowIds, setUnlocked] = useState<Set<string>>(new Set());

  // The teardown path reads the live launch without re-running the unmount effect on every
  // launch — a dependency on `active` would fire manualClose the moment a launch resolved.
  const activeRef = useRef(active);
  activeRef.current = active;
  const scriptedRunRef = useRef(scriptedRun);
  scriptedRunRef.current = scriptedRun;

  // Abandoning a task is the normal case on a list (unlike the single action card this moved
  // off, where completion was the only exit), so the tab is closed on teardown too.
  useEffect(
    () => () => {
      if (activeRef.current) finishManualPortal(activeRef.current.launchId);
    },
    []
  );

  useEffect(() => {
    if (!active) return;
    return onManualPortalResult(active.launchId, () => {
      // The HUD's send (or a closed tab's drafts) ends the launch. The captured values are the
      // slideout's business — it owns the action write — and the card's job is done once the
      // task is unlocked, which the ack already did.
      setActive(null);
    });
  }, [active]);

  // After a task-flow recording, other referrals may have been waiting on that exact script
  // (OLIO-3130). Toasts carry no actions, so the prompt is a pointer to the surface that has
  // one — silent when the fresh row counts nothing, and on any fetch failure: this is a
  // courtesy nudge riding a recording that already succeeded, never an error of its own.
  const offerStalledRerun = useCallback(
    async (sourceSystem: string) => {
      try {
        const rows = await indexPortalConnections();
        const stalled = rows.find((row) => row.sourceSystem === sourceSystem)?.stalledRetrievals ?? 0;
        if (stalled === 0) return;
        addToast({
          text: `${stalled} other referral${stalled === 1 ? ' was' : 's were'} waiting on this recording — re-run ${
            stalled === 1 ? 'it' : 'them'
          } from Manage Connected Portals.`,
        });
      } catch {
        // Nothing to say — see above.
      }
    },
    [addToast]
  );

  // The extension-mode runs, ported from the retired retrieval card. Replay executes the
  // saved script against the live entry URL; record arms the recorder and runs as long as
  // the person takes in the portal tab. Either way the captured values (documents uploaded
  // first) stage on the action via save_progress — the refreshed run leads with the review
  // form.
  const runScripted = useCallback(
    async ({
      workflowId,
      actionId,
      entryUrl,
      sourceSystem,
      portalPatientName,
      scriptId,
      produces,
    }: ManualLaunchRequest) => {
      const outputFields = portalCollectionOutputs(produces ?? []);
      const expectedOutputs = outputFields.map((output) => output.name);
      const documentOutputs = expectedOutputs.filter((name) => name === ATTACHMENTS_VALUE_NAME);
      const params = { patient_name: portalPatientName ?? '' };
      const context = { workflowId, actionId, sourceSystem, retrievalPointer: entryUrl };
      setScriptedRun({ workflowId, mode: scriptId ? 'replay' : 'record' });
      try {
        let raw: Record<string, unknown>;
        if (scriptId) {
          const detail = await showPortalScript(scriptId);
          raw = await runPortalRetrieval({
            script: { entryUrl: detail.entryUrl, body: detail.body, retrievalUrl: detail.retrievalUrl },
            // The retrieval pointer is always a resolved URL (validated at api ingress), so
            // it's the live entry point for replay — the extension prefers it over the
            // script's stale record-time entryUrl.
            entryUrl,
            params,
            context,
            expectedOutputs,
            documentOutputs,
            outputFields,
          });
        } else {
          raw = await runPortalRetrieval({
            record: true,
            entryUrl,
            params,
            context,
            expectedOutputs,
            documentOutputs,
            outputFields,
          });
        }
        const values = await uploadPortalDocuments(raw, environment?.OLIO_FILES_DOMAIN);
        const workflow = await saveWorkflowActionProgress({ workflowId, actionId, outputs: values });
        queryClient.setQueryData(workflowQueryKeys.show(workflowId), workflow);
        queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists });
        if (!scriptId) {
          // The run just recorded a script, so the launch pane's script lookup and the
          // Connected Portals readiness rows are both stale.
          queryClient.invalidateQueries({ queryKey: portalScriptQueryKeys.list(sourceSystem) });
          queryClient.invalidateQueries({ queryKey: portalConnectionQueryKeys.list() });
          void offerStalledRerun(sourceSystem);
        }
      } catch (error) {
        const fallback = scriptId ? 'Portal retrieval failed.' : 'Portal recording failed.';
        addToast({ text: error instanceof Error ? error.message : fallback });
      } finally {
        setScriptedRun(null);
      }
    },
    [addToast, environment?.OLIO_FILES_DOMAIN, offerStalledRerun, queryClient]
  );

  const launch = useCallback(
    async (request: ManualLaunchRequest) => {
      const { workflowId, actionId, entryUrl, sourceSystem, portalPatientName } = request;
      if (activeRef.current || scriptedRunRef.current) {
        addToast({ text: 'Close the portal tab you already have open before opening another.' });
        return;
      }
      if (request.record || request.scriptId) {
        await runScripted(request);
        return;
      }
      setLaunchingWorkflowId(workflowId);
      try {
        // The same declaration the retrieval form renders, described to the HUD: it has no idea
        // what any field means, so the datatype and the options travel with it. The field set
        // is this portal's configured selection (OLIO-3074); attachments ride the capture
        // affordance, not the typed form.
        const facilityOptions = allowedFacilities.map((group) => ({ value: group.id, label: group.name }));
        const fields: ManualPortalField[] = retrievalFieldsFor(portalSystems, sourceSystem)
          .filter((field): field is TypedRetrievalField => field.type !== 'documents')
          .map(({ name, label, type, cardinality }) => ({
            name,
            label,
            cardinality,
            type: type === 'facility' ? 'select' : type === 'ssn' ? 'text' : type,
            ...(type === 'facility' ? { options: facilityOptions } : {}),
            ...(type === 'select' ? { options: GENDERS } : {}),
          }));
        const { launchId } = await launchManualPortal({
          entryUrl,
          expectedOutputs: fields.map((field) => field.name),
          fields,
          params: { patient_name: portalPatientName ?? '' },
          context: { workflowId, actionId, sourceSystem, retrievalPointer: entryUrl },
        });
        setActive({ launchId, workflowId });
        setUnlocked((current) => new Set(current).add(workflowId));
      } catch (error) {
        addToast({ text: error instanceof Error ? error.message : 'Could not open the portal tab.' });
      } finally {
        setLaunchingWorkflowId(null);
      }
    },
    [addToast, allowedFacilities, portalSystems, runScripted]
  );

  const close = useCallback(() => {
    if (activeRef.current) finishManualPortal(activeRef.current.launchId);
    setActive(null);
  }, []);

  const value = useMemo(
    () => ({
      activeWorkflowId: active?.workflowId ?? null,
      launchingWorkflowId,
      recordingWorkflowId: scriptedRun?.mode === 'record' ? scriptedRun.workflowId : null,
      replayingWorkflowId: scriptedRun?.mode === 'replay' ? scriptedRun.workflowId : null,
      unlockedWorkflowIds,
      launch,
      close,
    }),
    [active, launchingWorkflowId, scriptedRun, unlockedWorkflowIds, launch, close]
  );

  return <ManualLaunchContext.Provider value={value}>{children}</ManualLaunchContext.Provider>;
}

export function useManualLaunch(): ManualLaunchState {
  const context = useContext(ManualLaunchContext);
  if (!context) throw new Error('useManualLaunch must be used inside a ManualLaunchProvider');
  return context;
}
